import MarkerClusterer from '@google/markerclustererplus';
import throttle from 'lodash/debounce';
import createFacilityPoiHandler from '@PROJECT_ROOT/assets/mobile/js/shared/createFacilityPoiHandler';
import createSimplePrivateGallery from '@PROJECT_ROOT/assets/desktop/js/search/simplePrivateGallery';
import getAdriaticStyles from '@PROJECT_ROOT/assets/shared/js/map/GoogleMapsStyles';
import makeCreateBeachMarker from '@PROJECT_ROOT/assets/mobile/js/shared/beachMarker';
import makeRenderBeachDetails from '@PROJECT_ROOT/assets/mobile/js/shared/beachDetails';
import makeShowMapInfoWindow from '@PROJECT_ROOT/assets/mobile/js/shared/mapInfoWindow';
import setClusterIcon from '@PROJECT_ROOT/assets/mobile/js/shared/clusterIcon';

export default function GoogleMap(google, mapContainer, options) {
    const { latitude, longitude, markerIconURL, mapMarkersData } = options;
    const objectCoordinates = new google.maps.LatLng(latitude, longitude);
    const mapOptions = getMapOptions(google, objectCoordinates);
    const map = new google.maps.Map(mapContainer, mapOptions);

    setMarker(google, objectCoordinates, map, markerIconURL);

    if (!mapMarkersData) {
        return;
    }

    const { mapIconURLs, beachMarkerData, facilityMarkerData } = mapMarkersData;

    showBeachMarkersOnMap(google, map, beachMarkerData, mapIconURLs);
    initializeToggleFacilityMarkers(google, map, facilityMarkerData, mapIconURLs);
}

function getMapOptions(google, objectCoordinates) {
    return {
        center: objectCoordinates,
        zoom: 16,
        minZoom: 7,
        mapTypeId: google.maps.MapTypeId.ROADMAP,
        mapTypeControl: true,
        panControl: false,
        zoomControl: true,
        scaleControl: true,
        streetViewControl: true,
        overviewMapControl: false,
        scrollwheel: false,
        styles: getAdriaticStyles(),
    };
}

function setMarker(google, objectCoordinates, map, markerIconURL) {
    return new google.maps.Marker({
        position: objectCoordinates,
        map,
        icon: {
            url: encodeURI(markerIconURL),
            scaledSize: new google.maps.Size(29, 39),
        },
    });
}

function createMarkerHandler(google, map, markers, clusterIconURL, clusterActiveIconURL) {
    let markerClusterer = null;

    return {
        showMarkers() {
            /**
             * @see https://github.com/mikesaidani/marker-clusterer-plus/blob/master/src/markerclusterer.js
             */
            markerClusterer = new MarkerClusterer(map, markers, {
                maxZoom: 14,
                styles: [
                    {
                        url: clusterIconURL,
                        width: 47,
                        height: 47,
                        textColor: '#000',
                        textSize: 12,
                    },
                ],
            });

            markerClusterer.addListener('mouseover', (cluster) => {
                setClusterIcon(cluster, clusterActiveIconURL);
            });

            markerClusterer.addListener('mouseout', (cluster) => {
                setClusterIcon(cluster, clusterIconURL);
            });
        },
        hideMarkers() {
            if (markerClusterer === null) {
                markers.forEach((marker) => {
                    marker.setMap(null);
                });

                return;
            }

            markerClusterer.clearMarkers();
        },
    };
}

function showBeachMarkersOnMap(
    google,
    map,
    beachMarkerData,
    { beachIconURL, beachActiveIconURL, clusterIconURL, clusterActiveIconURL },
) {
    const {
        noBeachImageURL,
        beachMarkers,
        facilityHasBeach,
    } = beachMarkerData;
    const createBeachMarker = makeCreateBeachMarker({
        google,
        googleMap: null, // inicijalno se markeri ne postavljaju na kartu
        beachIconURL,
        beachActiveIconURL,
    });
    const renderBeachDetails = makeRenderBeachDetails(createSimplePrivateGallery, noBeachImageURL);
    const showMapInfoWindow = makeShowMapInfoWindow(google, map);

    let googleMapsBeachMarkers = [];
    googleMapsBeachMarkers = beachMarkers.map(
        (beachMarker) => createBeachMarker(
            beachMarker,
            onBeachMarkerClick.bind(
                null,
                renderBeachDetails,
                showMapInfoWindow,
                () => googleMapsBeachMarkers,
                beachMarker,
            ),
        ),
    );

    const beachMarkerHandler = createMarkerHandler(google, map, googleMapsBeachMarkers, clusterIconURL, clusterActiveIconURL);
    const poiBeachFilterButton = document.getElementById('poiBeachFilterButton');
    const poiBeachFilterIcon = document.getElementById('poiBeachFilterIcon');

    if (facilityHasBeach) {
        beachMarkerHandler.showMarkers();
    } else {
        poiBeachFilterButton.classList.add('map-filter-button_inactive');
        poiBeachFilterIcon.classList.add('map-filter-button__beach-inactive-icon');
        poiBeachFilterButton.classList.remove('js-beachActive');
        poiBeachFilterIcon.classList.remove('map-filter-button__beach-icon');
    }

    initializeToggleBeachMarkers(poiBeachFilterButton, poiBeachFilterIcon, map, beachMarkerHandler);
}

function onBeachMarkerClick(
    renderBeachDetails,
    showMapInfoWindow,
    getGoogleMapsBeachMarkers,
    beachMarker,
    clickedMarker,
) {
    const beachDetailsContent = renderBeachDetails({
        beachGallery: beachMarker.gallery,
        beachDescriptions: beachMarker.descriptions,
    });

    showMapInfoWindow(Promise.resolve(beachDetailsContent), clickedMarker);

    getGoogleMapsBeachMarkers().forEach((marker) => {
        marker.removeHighlight();
    });

    clickedMarker.highlight();
}

function initializeToggleBeachMarkers(poiBeachFilterButton, poiBeachFilterIcon, map, beachMarkerHandler) {
    poiBeachFilterButton.addEventListener('click', () => {
        if (poiBeachFilterButton.classList.contains('js-beachActive')) {
            poiBeachFilterButton.classList.remove('js-beachActive');
            poiBeachFilterIcon.classList.remove('map-filter-button__beach-icon');
            poiBeachFilterButton.classList.add('map-filter-button_inactive');
            poiBeachFilterIcon.classList.add('map-filter-button__beach-inactive-icon');
            beachMarkerHandler.hideMarkers();
        } else {
            poiBeachFilterButton.classList.add('js-beachActive');
            poiBeachFilterIcon.classList.add('map-filter-button__beach-icon');
            poiBeachFilterButton.classList.remove('map-filter-button_inactive');
            poiBeachFilterIcon.classList.remove('map-filter-button__beach-inactive-icon');
            beachMarkerHandler.showMarkers();
        }
    });
}

function initializeToggleFacilityMarkers(google, map, facilityMarkerData, mapIconURLs) {
    const poiFacilityFilterButton = document.getElementById('poiFacilityFilterButton');
    const poiFacilityFilterIcon = document.getElementById('poiFacilityFilterIcon');
    const facilityPoiHandler = createFacilityPoiHandler(google, map, facilityMarkerData, mapIconURLs);

    /**
     * Throttle je metoda čiji je zadatak da se određena funkcija izvrši samo jednom u zadanom intervalu, neovisno
     * o tome koliko puta će korisnik pokrenuti neki event (npr: učitavanje novih rezultata pomicanjem po karti), što
     * znantno poboljšava performanse same aplikacije.
     *
     * U ovom slučaju funkcija će se izvršiti svako 200 milisekundi.
     *
     * @see https://lodash.com/docs/4.17.15#throttle
     */
    const showMarkersThrottled = throttle(facilityPoiHandler.showMarkers, 200);
    let mapZoomChangedListener = null;
    let mapDragendListener = null;

    poiFacilityFilterButton.addEventListener('click', () => {
        if (poiFacilityFilterButton.classList.contains('js-facilityInactive')) {
            poiFacilityFilterButton.classList.remove('js-facilityInactive');
            poiFacilityFilterButton.classList.remove('map-filter-button_inactive');
            poiFacilityFilterIcon.classList.remove('map-filter-button__facility-inactive-icon');
            poiFacilityFilterIcon.classList.add('map-filter-button__facility-icon');

            showMarkersThrottled();

            mapZoomChangedListener = map.addListener('zoom_changed', () => {
                google.maps.event.addListenerOnce(map, 'bounds_changed', () => {
                    showMarkersThrottled();
                });
            });
            mapDragendListener = map.addListener('dragend', () => {
                showMarkersThrottled();
            });
        } else {
            poiFacilityFilterButton.classList.add('js-facilityInactive');
            poiFacilityFilterButton.classList.add('map-filter-button_inactive');
            poiFacilityFilterIcon.classList.add('map-filter-button__facility-inactive-icon');
            poiFacilityFilterIcon.classList.remove('map-filter-button__facility-icon');

            facilityPoiHandler.hideMarkers();

            google.maps.event.removeListener(mapZoomChangedListener);
            google.maps.event.removeListener(mapDragendListener);
        }
    });
}
